// src/components/AboutView.tsx
// REPOSITIONING PASS v2 — rebuilt around the locked About-page copy block.
// This supersedes the v1 rewrite: the source text now explicitly names
// outcome-based/take-rate pricing ("building toward flexible models"),
// reopening the pricing-mention hold from the earlier alignment review.
// That hold was a sequencing caution, not a hard rule — the founder has
// now made the call to surface it here, hedged as "building toward,"
// which matches the strategy doc's own measured-language requirement.
// Kept: Section 2 "Where We Started" (execution-under-control narrative,
// already on-thesis) and Section 3 "How We Build" (build-log/transparency
// values, unaffected by the positioning shift) — reworded only where they
// still touched retired language, not restructured.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SEO } from './SEO';

const FDW_MCP_URL = 'https://fivedollarworkday.com/mcp';

export const AboutView: React.FC = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="About — Five Dollar Workday"
        description="Five Dollar Workday is the control and execution layer that lets service businesses turn AI agents into a controlled workforce — completing scoped work inside boundaries you set, while you remain merchant of record."
      />

      <main className="relative bg-brand-white text-brand-black font-sans selection:bg-brand-accent selection:text-white">

        {/* ── 1. THE THESIS ────────────────────────────────────────── */}
        <section className="px-6 md:px-12 py-32 border-b border-brand-border">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-gray mb-8">
              Our philosophy
            </p>
            <p className="text-3xl md:text-5xl font-semibold leading-tight text-brand-black mb-12 tracking-tight">
              Five Dollar Workday helps service businesses turn services like NDA reviews or monthly closes into installable work packages. Agents do the work. You keep control.
            </p>
            <p className="text-xl text-brand-charcoal font-light leading-relaxed max-w-3xl">
              We help consultancies, agencies, specialist firms, and transactional practices package their services as executable outcomes. Agents can discover, qualify, and complete scoped work — but only inside the precise boundaries you set.
            </p>
          </div>
        </section>

        {/* ── 2. WHAT YOU KEEP ─────────────────────────────────────── */}
        <section className="px-6 md:px-12 py-24 border-b border-brand-border bg-brand-surface">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-4 max-w-3xl">
              <p className="text-2xl md:text-3xl font-medium text-brand-black tracking-tight">
                You stay in command of the work and the client relationship.
              </p>
              <p className="text-2xl md:text-3xl font-medium text-brand-black tracking-tight">
                You remain the merchant of record.
              </p>
            </div>
          </div>
        </section>

        {/* ── 3. WHERE WE STARTED ──────────────────────────────────── */}
        <section className="px-6 md:px-12 py-32 border-b border-brand-border">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-brand-gray mb-8">
              Where we started
            </h2>
            <p className="text-3xl md:text-4xl font-semibold leading-tight text-brand-black mb-12 tracking-tight">
              We started with the hardest transactions.
            </p>

            <div className="space-y-8 max-w-3xl">
              <p className="text-lg text-brand-charcoal font-light leading-relaxed">
                We didn't start by building profile pages. We started by building the infrastructure for physical commerce — where an AI agent had to read complex business rules, check inventory, and execute a purchase on behalf of a human without anyone touching a keyboard.
              </p>
              <p className="text-lg text-brand-charcoal font-light leading-relaxed">
                To make that safe, we had to build a system where AI couldn't guess and couldn't overstep. Every action had to be checked against strict, pre-authorized rules. If an agent tried to do something outside those bounds, the system stopped it cold until a human weighed in — and if it stayed inside them, the work actually got done.
              </p>
              <p className="text-lg text-brand-charcoal font-light leading-relaxed">
                That exact same foundation now powers Five Dollar Workday. Whether an agent is buying coffee, completing a scoped NDA review, or running a monthly close, it relies on the same core principle: agents can complete real work, but only exactly as far as your rules allow.
              </p>
            </div>
          </div>
        </section>

        {/* ── 4. HOW WE BUILD ──────────────────────────────────────── */}
        <section className="px-6 md:px-12 py-32 border-b border-brand-border bg-brand-surface">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-brand-gray mb-8">
              How we build
            </h2>
            <p className="text-3xl font-semibold leading-tight text-brand-black mb-12 tracking-tight">
              Everything goes on the public record.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
              <div className="border-t border-brand-border pt-6">
                <h3 className="text-base font-medium text-brand-black mb-3">Transparency is our moat</h3>
                <p className="text-sm text-brand-charcoal font-light leading-relaxed">
                  Every technical mistake, architecture shift, and product decision is recorded in our open build log. If you hide your engineering failures, you forfeit algorithmic trust.
                </p>
              </div>
              <div className="border-t border-brand-border pt-6">
                <h3 className="text-base font-medium text-brand-black mb-3">We use what we build</h3>
                <p className="text-sm text-brand-charcoal font-light leading-relaxed">
                  We run our own business on Five Dollar Workday — our outcomes, our rules, our agents completing real work under them. We are our own strictest testers.
                </p>
              </div>
              <div className="border-t border-brand-border pt-6">
                <h3 className="text-base font-medium text-brand-black mb-3">Honest boundaries</h3>
                <p className="text-sm text-brand-charcoal font-light leading-relaxed">
                  We specify exactly what agents can complete on your behalf, and exactly where they have to stop and ask. You stay the merchant of record. We never take the client relationship, the payment, or the liability from you.
                </p>
              </div>
            </div>

            <button
              onClick={() => navigate('/blog')}
              className="px-8 py-4 bg-transparent text-brand-charcoal border border-brand-border rounded-full hover:border-brand-black hover:text-brand-black transition-all text-sm font-medium"
            >
              Read the build log
            </button>
          </div>
        </section>

        {/* ── 5. ALIGNED INCENTIVES ────────────────────────────────── */}
        <section className="px-6 md:px-12 py-32 border-b border-brand-border">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-gray mb-8">
              How we're built to grow
            </p>
            <p className="text-2xl md:text-3xl text-brand-charcoal font-light leading-relaxed max-w-3xl mb-8">
              We believe the platform's success should be able to track the value it helps create. That's why we're building toward flexible models — including outcome-based and take-rate options, alongside traditional access — as trust in the system grows.
            </p>
          </div>
        </section>

        {/* ── 6. CORE BELIEF ───────────────────────────────────────── */}
        <section className="px-6 md:px-12 py-32 bg-brand-surface border-b border-brand-border">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-gray mb-8">
              Our core belief
            </p>
            <p className="text-3xl md:text-4xl font-semibold leading-tight text-brand-black mb-8 tracking-tight max-w-3xl">
              The winners will look like services firms rebuilt on software infrastructure.
            </p>
            <p className="text-xl text-brand-charcoal font-light leading-relaxed max-w-3xl">
              Most tools only help humans work faster. We exist so AI can actually finish the work — under your rules — with aligned incentives.
            </p>
          </div>
        </section>

        {/* ── 7. CONCLUSION CTA ────────────────────────────────────── */}
        <section className="px-6 md:px-12 py-32 bg-brand-white">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-2 mb-12">
              <p className="text-2xl md:text-3xl font-medium text-brand-black tracking-tight">Define your outcomes.</p>
              <p className="text-2xl md:text-3xl font-medium text-brand-black tracking-tight">Set your boundaries.</p>
              <p className="text-2xl md:text-3xl font-medium text-brand-black tracking-tight">Let AI execute what you allow.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={FDW_MCP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-brand-black text-white rounded-full hover:bg-brand-charcoal transition-all text-center text-sm font-medium"
              >
                Put your business to work →
              </a>
              <button
                onClick={() => navigate('/blog')}
                className="px-8 py-4 bg-transparent text-brand-charcoal border border-brand-border rounded-full hover:border-brand-black hover:text-brand-black transition-all text-center text-sm font-medium"
              >
                Read the build log
              </button>
            </div>
            <p className="mt-6 text-xs font-mono text-brand-gray">
              Takes 5 minutes · No code required
            </p>
          </div>
        </section>

      </main>
    </>
  );
};